import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Package, Calendar, Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = '/api';

const EQUIPMENT = [
  {
    id: 'elektrostimulator',
    name: 'Elektrostimulator',
    description: 'Naprava za električno stimulacijo mišic za rehabilitacijo in krepitev.',
    pricePerMonth: 60,
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=300&h=200&fit=crop'
  },
  {
    id: 'magnetna-blazina',
    name: 'Elektromagnetna blazina',
    description: 'Terapevtska blazina z magnetnim poljem za lajšanje bolečin.',
    pricePerMonth: 100,
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=300&h=200&fit=crop'
  }
];

const EquipmentRentalModal = ({ isOpen, onClose }) => {
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    startDate: '',
    endDate: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const toggleEquipment = (id) => {
    setSelectedEquipment(prev => 
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedEquipment.length === 0) {
      toast.error('Izberite vsaj eno opremo');
      return;
    }

    setIsSubmitting(true);

    try {
      const equipmentNames = selectedEquipment.map(id => 
        EQUIPMENT.find(eq => eq.id === id)?.name
      ).join(', ');

      // Send contact message with equipment rental request
      await axios.post(`${API_URL}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `IZPOSOJA OPREME\n\nOprema: ${equipmentNames}\nOd: ${formData.startDate}\nDo: ${formData.endDate}\n\nOpombe: ${formData.notes || 'Brez opomb'}`
      });

      setIsSuccess(true);
      toast.success('Povpraševanje uspešno poslano!');
      
      // Reset form after 2 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({ name: '', email: '', phone: '', startDate: '', endDate: '', notes: '' });
        setSelectedEquipment([]);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error sending rental request:', error);
      toast.error('Napaka pri pošiljanju. Prosimo, pokličite nas.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#1a365d] text-white p-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6" />
            <h2 className="text-lg font-bold">Izposoja opreme</h2>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-2 rounded-full transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isSuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-[#1a365d] mb-2">Povpraševanje poslano!</h3>
            <p className="text-slate-600">Kontaktirali vas bomo v najkrajšem možnem času.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Equipment Selection */}
            <div>
              <label className="block text-sm font-semibold text-[#1a365d] mb-3">
                Izberite opremo *
              </label>
              <div className="space-y-3">
                {EQUIPMENT.map(eq => (
                  <button
                    key={eq.id}
                    type="button"
                    onClick={() => toggleEquipment(eq.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                      selectedEquipment.includes(eq.id)
                        ? 'border-[#f87171] bg-[#f87171]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <img loading="lazy" decoding="async" 
                      src={eq.image} 
                      alt={eq.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-[#1a365d]">{eq.name}</h4>
                      <p className="text-sm text-slate-500">{eq.description}</p>
                      <p className="text-sm font-medium text-[#f87171] mt-1">{eq.pricePerMonth}€ / mesec</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedEquipment.includes(eq.id)
                        ? 'border-[#f87171] bg-[#f87171]'
                        : 'border-slate-300'
                    }`}>
                      {selectedEquipment.includes(eq.id) && (
                        <CheckCircle className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#1a365d] mb-2">
                  Od datuma *
                </label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={e => setFormData({...formData, startDate: e.target.value})}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1a365d] mb-2">
                  Do datuma *
                </label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={e => setFormData({...formData, endDate: e.target.value})}
                  required
                  min={formData.startDate || new Date().toISOString().split('T')[0]}
                  className="rounded-xl"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#1a365d] mb-2">
                  Ime in priimek *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Vaše ime"
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1a365d] mb-2">
                  E-pošta *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  required
                  placeholder="vas@email.com"
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1a365d] mb-2">
                  Telefon *
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({...formData, phone: e.target.value})}
                  required
                  placeholder="+386 40 123 456"
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1a365d] mb-2">
                  Opombe (neobvezno)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({...formData, notes: e.target.value})}
                  placeholder="Dodatne informacije ali vprašanja..."
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#f87171] resize-none"
                  rows={3}
                />
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting || selectedEquipment.length === 0}
              className="w-full bg-[#f87171] hover:bg-[#f87171]/90 text-white rounded-xl py-6 font-semibold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Pošiljam...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Pošlji povpraševanje
                </>
              )}
            </Button>

            <p className="text-xs text-slate-500 text-center">
              Po prejemu povpraševanja vas bomo kontaktirali za potrditev razpoložljivosti in dogovor o prevzemu.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default EquipmentRentalModal;
